import { BoxProps, Button, IconButton, ModalBody, ModalFooter, ModalHeader, Text } from "@chakra-ui/react"
import React from "react"
import useAddToList from "../../../../../../common/hooks/useAddToList"
import { useBrandedTranslation } from "../../../../../../common/hooks/useBrandedTranslation"
import useModal from "../../../../../../common/hooks/useModal"
import CustomTooltip from "../../../../../shared/CustomTooltip"
import LoadingIcon from "../../../../../shared/DownloadButton/LoadingIcon"
import { CAddToList, CAddedToList } from "../../../../../shared/Icons"

type Props = BoxProps & {
  articleId: string | null
  productId: string
  hideContainerHighlight?: () => void
  shouldAllowAddToList?: () => boolean
}

const AddToListButton = ({ articleId, productId, hideContainerHighlight, shouldAllowAddToList }: Props) => {
  const { t } = useBrandedTranslation()
  const { Modal, onOpen: onOpenModal, onClose: onCloseModal } = useModal()
  /* Custom hook to manage adding an item to a list.
    If it's exact article match, pass the articleId to add a single article to the list.
    Otherwise pass the productId to add the whole product to the list. */
  const { addToList, isBeingAdded, isAdded, isNotAdded } = useAddToList(articleId ?? productId, true)

  const handleAddToList = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!shouldAllowAddToList?.() && isNotAdded) {
      return onOpenModal?.()
    }

    if (hideContainerHighlight) hideContainerHighlight()

    addToList()
  }

  const tooltipLabel = articleId ? "wishlist-add-article" : "wishlist-add-article_plural"
  const tooltipLabelConfirmed = articleId ? "wishlist-add-confirmation" : "wishlist-add-all-confirmation"

  return (
    <>
      <Modal modalContentStyleProps={{ mx: { base: "4", sm: "0" }, minW: { base: "auto", sm: "140" } }}>
        <ModalHeader mt="8">{t("acc-myLists-addArticle-popupTitle")}</ModalHeader>
        <ModalBody>
          <Text fontSize="sm">{t("acc-myLists-addArticle-popupText")}</Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={onCloseModal}>
            {t("lst-seconderyPopup-button")}
          </Button>
        </ModalFooter>
      </Modal>
      <CustomTooltip label={t(isAdded ? tooltipLabelConfirmed : tooltipLabel)}>
        <IconButton
          color="black"
          icon={isBeingAdded ? <LoadingIcon /> : isAdded ? <CAddedToList /> : <CAddToList />}
          position="absolute"
          left={{ base: 0, sm: "6" }}
          top={{ base: 4, sm: "6" }}
          isRound
          display="none"
          className="action-button"
          zIndex="docked"
          aria-label="Add to list"
          onClick={handleAddToList}
        />
      </CustomTooltip>
    </>
  )
}

export default AddToListButton
